// #3508 (station-monitor workstream) -- this tablet's station name, remembered
// in the browser so a tablet that has already been named never asks the server
// (or blocks on the network) again at load.
//
// The station GUID is util/presenceInstanceId.js's `scribe-fe.presenceInstanceId`.
// This cache holds only what the server confirmed for it: it is written AFTER a
// successful PUT/GET, never before, so a cache hit always means "the server
// knows this name". It is keyed by org because a station's name is per
// organisation (score-api lib/station-identity.js). A wiped browser loses the
// GUID and this cache together, which is exactly "a wiped tablet is a new
// station". Storage-guarded like presenceInstanceId: unavailable storage just
// means no cache, so the server is asked each load.
import { getPresenceInstanceId } from "./presenceInstanceId";

const KEY_PREFIX = "scribe-fe.stationIdentity.";

// Each entry records WHICH station GUID it was confirmed for. A GUID that was
// cleared while this entry survived (a partial wipe) is a new station: the
// entry no longer matches and reads as a miss, so the new GUID is asked for and
// named. Without this, the new GUID would inherit a name the server never
// heard for it.
export function readCachedStationIdentity(orgId) {
  if (!orgId) return null;
  try {
    const raw = localStorage.getItem(KEY_PREFIX + orgId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.name !== "string" || !parsed.name) return null;
    if (parsed.stationId !== getPresenceInstanceId()) return null;
    return { name: parsed.name, isTest: !!parsed.isTest };
  } catch {
    return null;
  }
}

// #3520: anything showing the station name (the header) re-renders when the
// remembered name changes, so an admin rename shows up without a reload.
const listeners = new Set();
export function subscribeStationIdentity(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
function notifyStationIdentity() {
  for (const fn of [...listeners]) {
    try {
      fn();
    } catch {
      // One bad subscriber never blocks the others.
    }
  }
}

export function writeCachedStationIdentity(orgId, identity) {
  if (!orgId || !identity?.name) return;
  try {
    localStorage.setItem(KEY_PREFIX + orgId, JSON.stringify({ stationId: getPresenceInstanceId(), name: identity.name, isTest: !!identity.isTest }));
  } catch {
    // Non-fatal: the server still holds the name.
    return;
  }
  notifyStationIdentity();
}

/**
 * #3520: the server's heartbeat reply carries the station's current name. When an
 * admin renamed this station, swap the remembered name for it. Only touches an
 * entry that already exists for THIS station GUID (a tablet that has not been
 * named is never given a name here). Returns true when it changed the cache.
 */
export function refreshCachedStationName(orgId, name) {
  const cached = readCachedStationIdentity(orgId);
  if (!cached || typeof name !== "string" || !name || name === cached.name) return false;
  writeCachedStationIdentity(orgId, { ...cached, name });
  return true;
}

/**
 * The identity ({ name, isTest }) this browser last confirmed for THIS station in ANY org, or null.
 * #3632: a known tablet meeting a new org carries this over instead of asking again.
 */
export function readAnyCachedStationIdentity() {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith(KEY_PREFIX)) {
        const hit = readCachedStationIdentity(key.slice(KEY_PREFIX.length));
        if (hit) return hit;
      }
    }
  } catch {
    // ignore
  }
  return null;
}

/** The name this browser last used in ANY org, to pre-fill the box when a known tablet meets a new org. */
export function readAnyCachedStationName() {
  return readAnyCachedStationIdentity()?.name ?? null;
}

/** Can this browser keep the station GUID (and so its name) across loads? False in a storage-blocked browser, where the GUID lives in memory only. */
export function canPersistStationIdentity() {
  try {
    const probe = "scribe-fe.stationIdentity.probe";
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}
